/* ============================================================================
   CDS FORMS — enhancement engine for the customer-facing forms.

   Wraps Globo Form Builder embeds (left fully functional as the submission
   backend) in the CDS dossier experience: grouped sections, progress rail,
   file trays, and the submit / success cinematic.

   Contract with Globo (verified against globo.formbuilder.bundle):
     - document events: "globo.formbuilder.form.loaded", ".form.submitting",
       ".form.submitted", ".form.reset" (detail.formId = data-id)
     - field wrapper: .globo-form-control  (has-error / has-success classes)
     - inline errors: .help-block.error inside the control
     - submit button: button[type=submit].action.submit (gets .loading)

   Every enhancement is defensive: if Globo's markup changes, fields that
   can't be claimed fall into an "Additional details" group and the raw form
   keeps working with flat styling.
   ========================================================================== */
(function () {
  'use strict';

  var SHELLS = [];

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ------------------------------------------------------------ helpers -- */
  function controlInput(control) {
    return control.querySelector('input:not([type=hidden]), select, textarea');
  }
  function controlName(control) {
    var inp = controlInput(control);
    return inp ? (inp.getAttribute('name') || '') : '';
  }
  function isSubmitControl(control) {
    return !!control.querySelector('button[type="submit"], .action.submit');
  }
  function controlFilled(control) {
    var inputs = $all('input:not([type=hidden]), select, textarea', control);
    if (!inputs.length) return false;
    return inputs.some(function (el) {
      if (el.type === 'checkbox' || el.type === 'radio') return el.checked;
      if (el.type === 'file') return el.files && el.files.length > 0;
      if (el.tagName === 'SELECT') {
        var v = el.value;
        return v && !/^please select/i.test(el.options[el.selectedIndex] ? el.options[el.selectedIndex].text : '');
      }
      return String(el.value || '').trim().length > 0;
    });
  }
  function controlRequired(control) {
    var lab = control.querySelector('label, legend');
    if (lab && /\*/.test(lab.textContent)) return true;
    var inp = controlInput(control);
    return !!(inp && (inp.required || inp.getAttribute('aria-required') === 'true'));
  }

  /* --------------------------------------------------------- enhancement -- */
  function enhanceForm(shell, cfg, embed) {
    var app = embed.querySelector('.globo-form-app');
    var form = embed.querySelector('form.g-container');
    var container = embed.querySelector('.block-container');
    if (!app || !form || !container || embed.dataset.cdsfDone) return false;

    var controls = $all('.globo-form-control', container).filter(function (c) {
      return c.parentElement === container ||
             c.parentElement.closest('.globo-form-control') === null;
    });
    if (!controls.length) return false;
    embed.dataset.cdsfDone = '1';

    /* index controls by field name */
    var byName = {};
    var submitControl = null;
    controls.forEach(function (c) {
      if (isSubmitControl(c)) { submitControl = c; return; }
      var n = controlName(c);
      if (n && !byName[n]) byName[n] = c;
    });

    /* build groups */
    var frag = document.createDocumentFragment();
    var claimed = {};
    var groups = [];
    (cfg.groups || []).forEach(function (g, gi) {
      var members = (g.fields || []).map(function (n) { return byName[n]; })
        .filter(Boolean);
      if (!members.length) return;
      var sec = document.createElement('section');
      sec.className = 'cdsf-group';
      sec.id = 'cdsf-' + cfg.id + '-g' + gi;
      var required = members.some(controlRequired);
      sec.innerHTML =
        '<header class="cdsf-group__head">' +
          '<span class="cdsf-group__num">' + String(gi + 1).padStart(2, '0') + '</span>' +
          '<h3 class="cdsf-group__title"></h3>' +
          (required ? '' : '<span class="cdsf-group__optional">Optional</span>') +
        '</header>' +
        (g.hint ? '<p class="cdsf-group__hint"></p>' : '') +
        '<div class="cdsf-group__fields"></div>';
      sec.querySelector('.cdsf-group__title').textContent = g.title || ('Section ' + (gi + 1));
      if (g.hint) sec.querySelector('.cdsf-group__hint').textContent = g.hint;
      var fieldsWrap = sec.querySelector('.cdsf-group__fields');
      members.forEach(function (c) {
        claimed[controlName(c)] = true;
        fieldsWrap.appendChild(c);
      });
      (g.wide || []).forEach(function (n) {
        if (byName[n]) byName[n].classList.add('cdsf-span2');
      });
      frag.appendChild(sec);
      groups.push({ el: sec, title: g.title || '', members: members });
    });

    /* leftovers → additional group (defensive against app-side edits) */
    var leftovers = controls.filter(function (c) {
      return c !== submitControl && !claimed[controlName(c)];
    });
    if (leftovers.length) {
      var sec = document.createElement('section');
      sec.className = 'cdsf-group';
      sec.innerHTML =
        '<header class="cdsf-group__head">' +
          '<span class="cdsf-group__num">' + String(groups.length + 1).padStart(2, '0') + '</span>' +
          '<h3 class="cdsf-group__title">Additional details</h3>' +
        '</header><div class="cdsf-group__fields"></div>';
      var wrap = sec.querySelector('.cdsf-group__fields');
      leftovers.forEach(function (c) { wrap.appendChild(c); });
      frag.appendChild(sec);
      groups.push({ el: sec, title: 'Additional details', members: leftovers });
    }

    /* stacks: wrap listed consecutive controls into a single grid cell */
    (cfg.stack || []).forEach(function (names) {
      var members = names.map(function (n) { return byName[n]; }).filter(Boolean);
      if (members.length < 2) return;
      var cell = document.createElement('div');
      cell.className = 'cdsf-stack';
      members[0].parentNode.insertBefore(cell, members[0]);
      members.forEach(function (c) { cell.appendChild(c); });
    });

    /* synthetic Company toggle (UI-only checkbox; the text input is a real
       named field — disabled until checked so it never posts when unused) */
    if (cfg.company) {
      var afterCtl = byName[cfg.company.after || 'phone'];
      if (afterCtl && !shell.querySelector('.cdsf-company[data-for="' + cfg.id + '"]')) {
        var comp = document.createElement('div');
        comp.className = 'globo-form-control cdsf-company';
        comp.setAttribute('data-for', cfg.id);
        comp.innerHTML =
          '<div class="globo-form-input"><label class="cdsf-company__toggle">' +
            '<input type="checkbox" class="cdsf-company__check">' +
            '<span>THIS IS A COMPANY / BUSINESS BUILD</span></label></div>' +
          '<div class="cdsf-company__field">' +
            '<label>Company Name</label>' +
            '<div class="globo-form-input">' +
              '<input class="flat-input" type="text" name="company" autocomplete="organization" disabled></div>' +
          '</div>';
        afterCtl.parentNode.insertBefore(comp, afterCtl.nextSibling);
        var chk = comp.querySelector('.cdsf-company__check');
        var fieldWrap = comp.querySelector('.cdsf-company__field');
        var inp = fieldWrap.querySelector('input');
        chk.addEventListener('change', function () {
          comp.classList.toggle('is-open', chk.checked);
          inp.disabled = !chk.checked;
          if (chk.checked) setTimeout(function () { inp.focus(); }, 240);
          else inp.value = '';
        });
        /* Globo's backend drops field names that aren't registered in the
           form config, so the value rides along inside the message textarea
           (a registered field). Capture phase => runs before Globo
           serializes the form. Re-entrant: strips any prior Company line
           before prepending, so retries never stack. */
        var mergeInto = cfg.company.mergeInto;
        if (mergeInto) {
          form.addEventListener('submit', function () {
            var msg = form.querySelector('[name="' + mergeInto + '"]');
            if (!msg) return;
            var cleaned = msg.value.replace(/^Company: [^\n]*\n?\n?/, '');
            if (chk.checked && inp.value.trim()) {
              msg.value = 'Company: ' + inp.value.trim() + '\n\n' + cleaned;
            } else {
              msg.value = cleaned;
            }
          }, true);
        }
      }
    }

    container.insertBefore(frag, container.firstChild);

    /* submit → footer slot. CRITICAL: the button must STAY a descendant of
       form.g-container — Globo's submit handler does
       e.target.querySelector('button[type=submit]') and crashes if the
       button was moved outside the form. So the shell footer is pulled
       INTO the form, then the button moves into its slot. */
    var footerSlot = shell.querySelector('.cdsf-footer__cta[data-for="' + cfg.id + '"]');
    var moveNode = submitControl;
    if (!moveNode) {
      var btnEl = form.querySelector('button[type="submit"], .action.submit');
      if (btnEl) moveNode = btnEl;
    }
    if (moveNode && footerSlot) {
      if (!form.id) form.id = 'cdsf-gform-' + cfg.id;
      var shellFooter = footerSlot.closest('.cdsf-footer');
      if (shellFooter) form.appendChild(shellFooter);
      moveNode.classList.add('cdsf-submit-control');
      footerSlot.appendChild(moveNode);
      app.classList.add('cdsf-enhanced');
    }

    /* autocomplete / inputmode niceties */
    var AUTOS = { name: 'name', 'name-2': 'family-name', email: 'email', phone: 'tel', url: 'url', text: 'organization' };
    Object.keys(AUTOS).forEach(function (n) {
      var c = byName[n]; if (!c) return;
      var inp = controlInput(c); if (!inp) return;
      inp.setAttribute('autocomplete', AUTOS[n]);
      if (n === 'phone') inp.setAttribute('inputmode', 'tel');
      if (n === 'email') inp.setAttribute('inputmode', 'email');
    });
    (cfg.numeric || []).forEach(function (n) {
      var c = byName[n]; if (!c) return;
      var inp = controlInput(c); if (inp) inp.setAttribute('inputmode', 'decimal');
    });

    /* file trays */
    $all('input[type="file"]', container).forEach(function (input) {
      buildTray(input);
    });

    return groups;
  }

  function buildTray(input) {
    var holder = input.closest('.globo-form-input') || input.parentElement;
    var control = input.closest('.globo-form-control');
    if (!holder || holder.querySelector('.cdsf-tray')) return;
    var tray = document.createElement('div');
    tray.className = 'cdsf-tray';
    tray.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M12 16V4m0 0 4 4m-4-4-4 4"/><path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/></svg>' +
      '<span class="cdsf-tray__label">Drop file or <em>browse</em></span>' +
      '<span class="cdsf-tray__sub">PDF, JPG or PNG</span>' +
      '<span class="cdsf-tray__files"></span>';
    holder.appendChild(tray);
    tray.appendChild(input); /* input keeps its listeners; overlays the tray */
    if (control) control.classList.add('cdsf-has-tray');

    var chips = tray.querySelector('.cdsf-tray__files');
    function renderChips() {
      chips.innerHTML = '';
      var files = input.files ? Array.prototype.slice.call(input.files) : [];
      files.forEach(function (f) {
        var chip = document.createElement('span');
        chip.className = 'cdsf-file-chip';
        chip.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round"><path d="M20 6 9 17l-5-5"/></svg><span></span>';
        chip.querySelector('span').textContent = f.name;
        chips.appendChild(chip);
      });
      tray.classList.toggle('has-files', files.length > 0);
    }
    input.addEventListener('change', renderChips);
    ['dragenter', 'dragover'].forEach(function (ev) {
      tray.addEventListener(ev, function (e) { e.preventDefault(); tray.classList.add('is-drag'); });
    });
    ['dragleave', 'drop'].forEach(function (ev) {
      tray.addEventListener(ev, function (e) { e.preventDefault(); tray.classList.remove('is-drag'); });
    });
    tray.addEventListener('drop', function (e) {
      if (!e.dataTransfer || !e.dataTransfer.files || !e.dataTransfer.files.length) return;
      try {
        input.files = e.dataTransfer.files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (err) { /* older engines: fall through to click-to-browse */ }
    });
  }

  /* --------------------------------------------------------- progress ---- */
  function wireProgress(shell, state) {
    var railList = shell.querySelector('.cdsf-rail ol');
    var meterFill = shell.querySelector('.cdsf-rail .cdsf-meter-fill');
    var meterPct = shell.querySelector('.cdsf-rail [data-pct]');
    var mFill = shell.querySelector('.cdsf-mprog .cdsf-meter-fill');
    var mLabel = shell.querySelector('.cdsf-mprog__label strong');
    var mCount = shell.querySelector('.cdsf-mprog__label [data-count]');

    function activeGroups() {
      var pane = shell.querySelector('.cdsf-pane.is-visible');
      var embed = pane ? pane.querySelector('.globo-formbuilder')
                       : shell.querySelector('.globo-formbuilder');
      var key = (embed && embed.dataset.id) || Object.keys(state.groupsByForm)[0];
      return state.groupsByForm[key] || [];
    }

    function renderRail() {
      if (!railList) return;
      railList.innerHTML = '';
      activeGroups().forEach(function (g, i) {
        var li = document.createElement('li');
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = g.title;
        btn.addEventListener('click', function () {
          g.el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        li.appendChild(btn);
        railList.appendChild(li);
        g.li = li;
      });
    }

    function update() {
      var groups = activeGroups();
      if (!groups.length) return;
      var done = 0, activeSet = false;
      groups.forEach(function (g) {
        var req = g.members.filter(controlRequired);
        var complete = req.length
          ? req.every(controlFilled) && !g.el.querySelector('.has-error')
          : g.members.some(controlFilled);
        if (complete) done++;
        if (g.li) g.li.classList.toggle('is-done', complete);
      });
      var pct = Math.round((done / groups.length) * 100);
      if (meterFill) meterFill.style.width = pct + '%';
      if (meterPct) meterPct.textContent = pct + '%';
      if (mFill) mFill.style.width = pct + '%';
      if (mCount) mCount.textContent = done + '/' + groups.length;
      /* current section = first not-done, for the mobile label */
      var current = groups.find(function (g) { return g.li && !g.li.classList.contains('is-done'); }) || groups[groups.length - 1];
      if (mLabel && current) mLabel.textContent = current.title;
    }

    /* scroll spy for rail active state */
    var spy = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        activeGroups().forEach(function (g) {
          if (g.li) g.li.classList.toggle('is-active', g.el === en.target);
        });
      });
    }, { rootMargin: '-30% 0px -55% 0px' }) : null;

    function observe() {
      if (!spy) return;
      spy.disconnect();
      activeGroups().forEach(function (g) { spy.observe(g.el); });
    }

    shell.addEventListener('input', debounce(update, 140), true);
    shell.addEventListener('change', debounce(update, 60), true);
    state.refreshProgress = function () { renderRail(); observe(); update(); };
    state.refreshProgress();
  }

  function debounce(fn, ms) {
    var t; return function () { clearTimeout(t); t = setTimeout(fn, ms); };
  }

  /* ------------------------------------------------------ path chooser --- */
  function wirePaths(shell, state) {
    var paths = $all('.cdsf-path', shell);
    if (!paths.length) return;
    function select(key, scroll) {
      paths.forEach(function (p) {
        var on = p.dataset.path === key;
        p.classList.toggle('is-active', on);
        p.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      $all('.cdsf-pane', shell).forEach(function (pane) {
        pane.classList.toggle('is-visible', pane.dataset.pane === key);
      });
      if (state.refreshProgress) state.refreshProgress();
      if (scroll) {
        var doc = shell.querySelector('.cdsf-doc');
        if (doc) doc.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      try { history.replaceState(null, '', '#' + key); } catch (e) {}
    }
    paths.forEach(function (p) {
      p.addEventListener('click', function () { select(p.dataset.path, true); });
    });
    var initial = (location.hash || '').replace('#', '');
    if (!paths.some(function (p) { return p.dataset.path === initial; })) {
      initial = paths[0].dataset.path;
    }
    select(initial, false);
  }

  /* ------------------------------------------------- submit / success ---- */
  function wireSubmitCinematics(shell, state) {
    var overlay = shell.querySelector('.cdsf-success');
    if (!overlay) return;

    document.addEventListener('globo.formbuilder.form.submitting', function (e) {
      if (!ownsForm(shell, e)) return;
      shell.classList.add('is-submitting');
      /* if a tray hid app chrome, reveal it in case Globo shows upload UI */
      $all('.cdsf-has-tray', shell).forEach(function (c) { c.classList.remove('cdsf-has-tray'); });
    });

    document.addEventListener('globo.formbuilder.form.submitted', function (e) {
      if (!ownsForm(shell, e)) return;
      shell.classList.remove('is-submitting');
      var formId = e.detail && e.detail.formId;
      var copy = (state.cfgById[formId] && state.cfgById[formId].success) || state.defaultSuccess || {};
      if (copy.title) {
        var t = overlay.querySelector('.cdsf-success__title');
        if (t) t.innerHTML = copy.title;
      }
      var m = overlay.querySelector('.cdsf-success__msg');
      if (m) {
        m.textContent = copy.msg || '';
        m.hidden = !copy.msg;
      }
      overlay.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      var focusBtn = overlay.querySelector('.cdsf-btn');
      if (focusBtn) setTimeout(function () { focusBtn.focus(); }, 600);
    });

    /* validation failure: scroll to the first error so it never fails silently */
    document.addEventListener('globo.formbuilder.form.submitting', function (e) {
      if (!ownsForm(shell, e)) return;
      setTimeout(function () {
        var err = shell.querySelector('.globo-form-control.has-error');
        if (err) err.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 450);
    });

    function close() {
      overlay.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      var hero = shell.querySelector('.cdsf-hero');
      if (hero) hero.scrollIntoView({ behavior: 'smooth', block: 'start' });
      if (state.refreshProgress) state.refreshProgress();
    }
    $all('[data-cdsf-close]', overlay).forEach(function (b) {
      b.addEventListener('click', close);
    });
    overlay.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  function ownsForm(shell, e) {
    var id = e.detail && e.detail.formId;
    if (!id) {
      return e.detail && e.detail.form && shell.contains(e.detail.form);
    }
    return !!shell.querySelector('.globo-formbuilder[data-id="' + id + '"]');
  }

  /* ------------------------------------------------------------ reveals -- */
  function wireReveals(shell) {
    var els = $all('.cdsf-reveal', shell);
    if (!els.length) return;
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    els.forEach(function (el) { io.observe(el); });
  }

  /* --------------------------------------------------------------- boot -- */
  function bootShell(shell) {
    shell.classList.add('cdsf-js');
    var cfgEl = shell.querySelector('script.cdsf-config');
    var cfg = { forms: [] };
    try { cfg = JSON.parse(cfgEl.textContent); } catch (e) {}
    var state = {
      groupsByForm: {},
      cfgById: {},
      defaultSuccess: cfg.success || null,
      refreshProgress: null
    };
    (cfg.forms || []).forEach(function (f) { state.cfgById[f.id] = f; });

    function tryEnhance() {
      var pending = false;
      (cfg.forms || []).forEach(function (f) {
        if (state.groupsByForm[f.id]) return;
        var embed = shell.querySelector('.globo-formbuilder[data-id="' + f.id + '"]');
        if (!embed) return;
        var groups = enhanceForm(shell, f, embed);
        if (groups) {
          state.groupsByForm[f.id] = groups;
          if (state.refreshProgress) state.refreshProgress();
        } else if (!embed.dataset.cdsfDone) {
          pending = true;
        }
      });
      return !pending;
    }

    document.addEventListener('globo.formbuilder.form.loaded', function () {
      setTimeout(tryEnhance, 30);
    });
    /* fallback poll — app may have rendered before this script ran */
    var tries = 0;
    (function poll() {
      var complete = tryEnhance();
      var total = (cfg.forms || []).length;
      var done = Object.keys(state.groupsByForm).length;
      if ((complete && done === total) || tries++ > 80) {
        wireProgress(shell, state);
        wirePaths(shell, state);
        return;
      }
      setTimeout(poll, 250);
    })();

    wireSubmitCinematics(shell, state);
    wireReveals(shell);
  }

  function init() {
    $all('.cdsf').forEach(function (shell) {
      if (!shell.dataset.cdsfBoot) { shell.dataset.cdsfBoot = '1'; bootShell(shell); }
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else { init(); }
})();
